import { BILLING_MODELS, type BillingModel } from '@crm/contracts';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Plus, Receipt } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type DealView, type JobView, type PipelineView } from '../../lib/client.ts';
import { BILLING_LABEL } from '../../lib/record-data.ts';
import { StageStepper } from '../pipelines/stage-control.tsx';
import { StartPicker, useStartPick } from '../pipelines/pipeline-stage-picker.tsx';
import { RecordPlaces } from './record-places.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { EmptyState } from '../ui/empty-state.tsx';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { DealJobs } from './deal-bundles.tsx';

const money = (d: DealView) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: d.currency }).format(
    Number(d.value),
  );

/**
 * The Deals tab (RELAY-952): the project's payment lines, each with value, billing model and
 * a Deal Status picker; a row expands to the deal's bundles and jobs (`DealJobs`), where more of
 * either can be added.
 */
export function DealsTab({
  projectId,
  deals,
  jobs,
  dealPipeline,
  jobPipeline,
  shortlistPipeline,
  jobsOn,
  canWrite,
  canWriteJobs,
  canCreateJobs,
  onChanged,
}: {
  projectId: string;
  deals: DealView[];
  jobs: JobView[];
  dealPipeline: PipelineView | undefined;
  jobPipeline: PipelineView | undefined;
  shortlistPipeline: PipelineView | undefined;
  jobsOn: boolean;
  /** `customer.update` on the project: deals and bundles. */
  canWrite: boolean;
  /** `job.update` / `job.create`: the jobs inside a deal's row, as on the Jobs tab. */
  canWriteJobs: boolean;
  canCreateJobs: boolean;
  onChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const move = useMutation({
    mutationFn: (v: { id: string; stageId: string }) => api.deals.moveStatus(v.id, v.stageId),
    onSuccess: async () => {
      setError(null);
      await onChanged();
    },
    onError: (err) => setError(describeError(err)),
  });
  const toggle = (id: string) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <div className="flex flex-col gap-5" data-testid="deals-tab">
      {deals.length === 0 && (
        <EmptyState
          icon={Receipt}
          title="No deals yet."
          description="A deal is one payment line on the project: a placement fee, a retainer."
        />
      )}
      {deals.length > 0 && (
        <ul className="flex flex-col gap-4">
          {deals.map((d) => {
            const dealJobs = jobs.filter((j) => j.dealId === d.id);
            return (
              <li
                key={d.id}
                data-testid="deal-row"
                data-deal-id={d.id}
                className="bg-card border-border rounded-card shadow-card overflow-hidden border"
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-5 py-4 text-sm">
                  {jobsOn && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 rounded-lg"
                      aria-expanded={open.has(d.id)}
                      aria-label={`${open.has(d.id) ? 'Collapse' : 'Expand'} ${d.title}`}
                      onClick={() => toggle(d.id)}
                    >
                      {open.has(d.id) ? <ChevronDown /> : <ChevronRight />}
                    </Button>
                  )}
                  <span className="flex min-w-40 flex-1 items-center gap-3">
                    <span className="bg-brand-soft text-brand-soft-foreground grid size-9 shrink-0 place-items-center rounded-xl">
                      <Receipt className="size-4" aria-hidden />
                    </span>
                    <span className="min-w-0">
                      <span
                        className="block text-[15px] font-semibold break-words"
                        data-testid="deal-title"
                      >
                        {d.title}
                      </span>
                      <span className="text-muted-foreground block text-xs">
                        {BILLING_LABEL[d.billingModel]}
                      </span>
                    </span>
                  </span>
                  {/* Deal Status as the compact stepper; a narrow card wraps it onto its own line. */}
                  {dealPipeline && (
                    <div className="order-last flex w-full basis-full flex-col gap-1.5">
                      <StageStepper
                        size="compact"
                        pipeline={dealPipeline}
                        value={d.stageId}
                        disabled={!canWrite || move.isPending}
                        onMove={(stageId) => move.mutate({ id: d.id, stageId })}
                      />
                      <RecordPlaces
                        type="deal"
                        places={d.pipelines}
                        disabled={!canWrite || move.isPending}
                        onMove={(stageId) => move.mutate({ id: d.id, stageId })}
                      />
                    </div>
                  )}
                  <span className="bg-muted rounded-lg px-3 py-1.5 text-sm font-bold tabular-nums">
                    {money(d)}
                  </span>
                  {d.paidAt && (
                    <span className="text-muted-foreground text-xs">
                      Paid {new Date(d.paidAt).toLocaleDateString()}
                    </span>
                  )}
                  {jobsOn && (
                    <Badge variant="outline">
                      {dealJobs.length} job{dealJobs.length === 1 ? '' : 's'}
                    </Badge>
                  )}
                </div>
                {jobsOn && open.has(d.id) && (
                  <div
                    className="border-border bg-muted/40 border-t px-4 py-5 sm:px-6"
                    data-testid="deal-jobs"
                  >
                    <DealJobs
                      deal={d}
                      deals={deals}
                      jobs={dealJobs}
                      projectJobs={jobs}
                      jobPipeline={jobPipeline}
                      shortlistPipeline={shortlistPipeline}
                      canWrite={canWrite}
                      canWriteJobs={canWriteJobs}
                      canCreateJobs={canCreateJobs}
                      onChanged={onChanged}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
      {canWrite && <AddDealForm projectId={projectId} onCreated={onChanged} />}
    </div>
  );
}

/**
 * A new deal: title, value, currency, billing, and the Deal Flow pipeline it starts in with its
 * first stage (the default pipeline and its first open stage unless picked). An extra pipeline
 * gives the deal a place there; its status still starts at the default's first open stage.
 */
function AddDealForm({
  projectId,
  onCreated,
}: {
  projectId: string;
  onCreated: () => Promise<void>;
}) {
  const start = useStartPick('deal');
  const { pipelineId, stageId } = start.value;
  const [title, setTitle] = useState('');
  const [value, setValue] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [billing, setBilling] = useState<BillingModel>('placement_fee');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.projects.addDeal(projectId, {
        title: title.trim(),
        value: value.trim(),
        currency,
        billingModel: billing,
        ...(pipelineId && { pipelineId }),
        ...(stageId && { stageId }),
      }),
    onSuccess: async () => {
      setTitle('');
      setValue('');
      setError(null);
      await onCreated();
    },
    onError: (err) => setError(describeError(err)),
  });
  return (
    <form
      className="border-border bg-card/50 flex flex-wrap items-center gap-3 rounded-card border border-dashed p-4"
      data-testid="add-deal"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim() && value.trim()) create.mutate();
      }}
    >
      <Input
        aria-label="Deal title"
        placeholder="New deal title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full sm:w-64"
      />
      <Input
        aria-label="Deal value"
        placeholder="Value"
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-32"
      />
      <Input
        aria-label="Currency"
        value={currency}
        maxLength={3}
        onChange={(e) => setCurrency(e.target.value.toUpperCase())}
        className="w-20"
      />
      <Select value={billing} onValueChange={(v) => setBilling(v as BillingModel)}>
        <SelectTrigger className="w-44" aria-label="Billing model">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {BILLING_MODELS.map((b) => (
            <SelectItem key={b} value={b}>
              {BILLING_LABEL[b]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <StartPicker pick={start} />
      <Button
        type="submit"
        disabled={!title.trim() || !value.trim() || !stageId || create.isPending}
      >
        <Plus /> Add deal
      </Button>
      {error && (
        <p className="text-danger w-full text-sm" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
